import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/lib/stores/auth'

/**
 * PublicOnlyRoute - Route guard for public routes
 * 
 * Redirects authenticated users to /dashboard
 * Allows unauthenticated users to access public routes
 * 
 * CRITICAL: Does NOT redirect during OAuth callback flow
 * OAuth callbacks return to root `/` with query params (code=, access_token=, etc.)
 * We must allow the OAuth callback to complete before redirecting
 * 
 * Usage:
 * <Route element={<PublicOnlyRoute />}>
 *   <Route path="/" element={<Landing />} />
 *   <Route path="/signin" element={<SignIn />} />
 * </Route>
 */
export function PublicOnlyRoute() {
  const { isAuthenticated, isInitialized } = useAuthStore()
  const location = useLocation()
  const hasAuthResponse =
    location.search.includes('code=') ||
    location.search.includes('access_token=') ||
    location.search.includes('error=') ||
    location.search.includes('token=')

  // CRITICAL: Never redirect during OAuth callback
  // OAuth callbacks redirect to root `/` with query params
  // Supabase needs to process these params before we redirect
  const isOAuthCallback =
    location.pathname.startsWith('/auth/callback') ||
    hasAuthResponse

  // Allow OAuth callback to complete (don't redirect)
  if (isOAuthCallback) {
    if (location.pathname === '/' && hasAuthResponse) {
      return (
        <Navigate
          to={`/auth/callback${location.search}${location.hash}`}
          replace
        />
      )
    }

    return <Outlet />
  }

  // Only redirect if auth is initialized AND user is authenticated
  // Don't block rendering while auth initializes - let the page render immediately
  if (isInitialized && isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  // Allow access while auth initializes or when unauthenticated
  return <Outlet />
}
